import { apiFetch } from '../api/client';
import { askHelp, type HelpResponse } from '../api/help';
import { rateLimitError } from '../utils/apiErrors';
import { transcribeAudio } from '../utils/transcribe';
import {
  ResumeExpiredError,
  isNotResumable,
  type LessonPart,
  type RecapEntry,
  type ResumableResponse,
  type TranscribeAudioResponse,
} from './useLessonApi';
import type { Step } from '../utils/stepHandlers';

export interface StartAssessmentResponse {
  run_id: string;
  step: Step;
  step_version: number;
  completed: boolean;
  context_name: string;
  context_id: string;
  total_steps: number;
  // The parts the learner's completed lessons taught, in script order (BR-005).
  parts: LessonPart[];
  /** Present on a resume: progress per part, never scene text (BR-043). */
  recap?: RecapEntry[];
}

export interface AssessmentNextStepResponse {
  completed: boolean;
  run_id: string;
  step: Step;
  step_version: number;
  step_indexes?: number[];
}

export interface ASRResult {
  transcript: string;
  /** Engine confidence, or `null` when the engine has no trustworthy score. */
  confidence: number | null;
}

// Debrief response shapes — mirror backend `DebriefResponse` (S7.4 redesign).
// `overall_status` was intentionally removed; a run that reaches debrief is
// complete by construction. Per-step `result` is the only signal.
export interface DebriefFormPerformance {
  form_id: string;
  form_text: string | null;
  attempts: number;
  successes: number;
  latency_ms: number | null;
  confidence: number | null;
}

export type DebriefOutcome = 'handled' | 'took_a_few_tries' | 'not_handled';

export interface DebriefStepResult {
  step_index: number;
  step_spec_id: string;
  speaker: string;
  rd_ids: string[];
  // The part this step belongs to (its first required detail); null if it has none.
  part_id: string | null;
  // Backend emits HANDLED (≥1 form success) or PARTIAL (attempts, no success).
  // Persona turns and learner turns with zero attempts come back as null.
  // See backend/src/ai_tutor_service.py:1644 (RunStepResult assignment).
  result: 'HANDLED' | 'PARTIAL' | null;
  form_performances: DebriefFormPerformance[];
  // BR-024 outcome of a learner part; null for a character line. Help is the
  // separate handled_with_help flag.
  outcome: DebriefOutcome | null;
  // The part carries a note on form (BR-025). Always false until #264.
  form_note: boolean;
  // The learner asked what the character said before this turn (BR-024). Shown
  // instead of the attempt count when both apply.
  handled_with_help: boolean;
}

export interface DebriefFormStats {
  form_id: string;
  form_text: string | null;
  attempts: number;
  successes: number;
  success_rate: number | null;
}

// BR-024 outcome of one required part: the worst of its learner steps; a part never reached is not_handled.
export interface DebriefPart extends LessonPart {
  outcome: DebriefOutcome;
  form_note: boolean;
  handled_with_help: boolean;
}

export interface DebriefResponse {
  run_id: string;
  score_pct: number;
  per_step_results: DebriefStepResult[];
  form_stats: DebriefFormStats[];
  parts: DebriefPart[];
}

export const useAssessmentApi = (): {
  startAssessment: (
    contextId: string,
    signal?: AbortSignal
  ) => Promise<StartAssessmentResponse>;
  /** Is there an unfinished check-in for this context? Throws on a failed request. */
  getResumable: (
    contextId: string,
    signal?: AbortSignal
  ) => Promise<ResumableResponse>;
  /** Picks the check-in up at its current step; throws `ResumeExpiredError` past the window. */
  resumeRun: (
    runId: string,
    signal?: AbortSignal
  ) => Promise<StartAssessmentResponse>;
  nextAssessmentStep: (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult,
    signal?: AbortSignal
  ) => Promise<AssessmentNextStepResponse>;
  transcribeAudio: (
    audioBlob: Blob,
    languageCode?: string,
    signal?: AbortSignal
  ) => Promise<TranscribeAudioResponse>;
  fetchTextAudioUrl: (
    runId: string,
    text: string,
    locale: string,
    speed: 'natural' | 'slow',
    signal?: AbortSignal
  ) => Promise<string>;
  fetchDebrief: (
    runId: string,
    signal?: AbortSignal
  ) => Promise<DebriefResponse>;
  /** What did the character just say? (the server explains the line it holds). */
  askHelp: (
    runId: string,
    formId: string | null,
    signal?: AbortSignal
  ) => Promise<HelpResponse>;
} => {
  const startAssessment = async (
    contextId: string,
    signal?: AbortSignal
  ): Promise<StartAssessmentResponse> => {
    const response = await apiFetch(`/context/${contextId}/assess`, {
      method: 'POST',
      headers: { 'Idem-Key': crypto.randomUUID() },
      body: JSON.stringify({}),
      signal,
    });

    if (!response.ok) {
      if (response.status === 403) {
        const body = await response.json().catch(() => null);
        const msg =
          body?.detail?.message ?? body?.detail ?? 'Prerequisites not met';
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      const tooFast = await rateLimitError(response);
      if (tooFast) {
        throw tooFast;
      }
      throw new Error(`Failed to start assessment: ${response.statusText}`);
    }

    return response.json();
  };

  const getResumable = async (
    contextId: string,
    signal?: AbortSignal
  ): Promise<ResumableResponse> => {
    const response = await apiFetch(
      `/assessments/context/${contextId}/resumable`,
      { signal }
    );
    if (!response.ok) {
      throw new Error(
        `Failed to check for a check-in to resume: ${response.status}`
      );
    }
    return response.json();
  };

  const resumeRun = async (
    runId: string,
    signal?: AbortSignal
  ): Promise<StartAssessmentResponse> => {
    const response = await apiFetch(`/assessments/${runId}/resume`, {
      method: 'POST',
      headers: { 'Idem-Key': crypto.randomUUID() },
      signal,
    });
    if (await isNotResumable(response)) {
      throw new ResumeExpiredError();
    }
    if (!response.ok) {
      throw new Error(`Failed to resume check-in: ${response.statusText}`);
    }
    return response.json();
  };

  const nextAssessmentStep = async (
    runId: string,
    stepVersion: number,
    asrResult?: ASRResult,
    signal?: AbortSignal
  ): Promise<AssessmentNextStepResponse> => {
    // Empty body when no transcript — process_next_turn checks transcript field,
    // equivalent to { event: 'advance' } in the lesson API
    const body = asrResult
      ? { transcript: asrResult.transcript, confidence: asrResult.confidence }
      : {};

    const response = await apiFetch(`/assessments/${runId}/next`, {
      method: 'POST',
      headers: {
        'Step-Version': stepVersion.toString(), // Note: Step-Version, not X-Step-Version
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('Version conflict: step_version mismatch');
      }
      throw new Error(`Failed to advance assessment: ${response.statusText}`);
    }

    return response.json();
  };

  const fetchTextAudioUrl = async (
    runId: string,
    text: string,
    locale: string,
    speed: 'natural' | 'slow',
    signal?: AbortSignal
  ): Promise<string> => {
    const response = await apiFetch('/audio/text', {
      method: 'POST',
      body: JSON.stringify({ run_id: runId, text, locale, speed }),
      signal,
    });
    if (!response.ok) {
      const tooFast = await rateLimitError(response);
      if (tooFast) {
        throw tooFast;
      }
      throw new Error(`Failed to fetch audio: ${response.statusText}`);
    }
    const data: { url: string; duration_ms: number } = await response.json();
    return data.url;
  };

  const fetchDebrief = async (
    runId: string,
    signal?: AbortSignal
  ): Promise<DebriefResponse> => {
    const response = await apiFetch(`/runs/${runId}/debrief`, { signal });
    if (!response.ok) {
      throw new Error(`Failed to fetch debrief: ${response.statusText}`);
    }
    return response.json();
  };

  return {
    startAssessment,
    getResumable,
    resumeRun,
    nextAssessmentStep,
    transcribeAudio,
    fetchTextAudioUrl,
    fetchDebrief,
    askHelp,
  };
};
